import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { FundListSortColumn, SortDirection } from '@/types/fund.types';

interface FundListStateValue {
  sortColumn: FundListSortColumn;
  sortDirection: SortDirection;
  expandedFundIds: ReadonlySet<string>;
  /** Same column flips direction; a new column starts ascending. */
  toggleSort: (column: FundListSortColumn) => void;
  toggleFund: (fundId: string) => void;
}

const FundListStateContext = createContext<FundListStateValue | null>(null);

/**
 * The fund list's sort order and expanded rows, held in memory above the
 * routes so they survive a trip to Alerts or Disclaimers and back.
 *
 * Mounted inside AppLayout, which exists only while the user is signed in, so
 * signing out (or timing out) discards it along with everything else: the next
 * person to sign in on this browser never inherits someone else's view. A page
 * reload also starts fresh, which is the trade-off for keeping it out of the
 * URL and out of storage.
 */
export function FundListStateProvider({ children }: { children: ReactNode }) {
  const [sort, setSort] = useState({
    column: FundListSortColumn.TICKER,
    direction: SortDirection.ASCENDING,
  });
  const [expandedFundIds, setExpandedFundIds] = useState<ReadonlySet<string>>(() => new Set());

  const toggleSort = useCallback((column: FundListSortColumn) => {
    setSort((current) =>
      current.column === column
        ? {
            column,
            direction:
              current.direction === SortDirection.ASCENDING
                ? SortDirection.DESCENDING
                : SortDirection.ASCENDING,
          }
        : { column, direction: SortDirection.ASCENDING },
    );
  }, []);

  const toggleFund = useCallback((fundId: string) => {
    setExpandedFundIds((current) => {
      const next = new Set(current);
      if (next.has(fundId)) next.delete(fundId);
      else next.add(fundId);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      sortColumn: sort.column,
      sortDirection: sort.direction,
      expandedFundIds,
      toggleSort,
      toggleFund,
    }),
    [sort, expandedFundIds, toggleSort, toggleFund],
  );

  return <FundListStateContext.Provider value={value}>{children}</FundListStateContext.Provider>;
}

export function useFundListState(): FundListStateValue {
  const context = useContext(FundListStateContext);
  if (!context) {
    throw new Error('useFundListState must be used inside a FundListStateProvider');
  }
  return context;
}
